import { ROUTER_PATH } from "@config/router";
import {
  isNewsRoute,
  isOnboardingRoute,
  isPricingRoute,
} from "@/src/hooks/onboarding/isOnboardingRoute";
import { isAppAuxiliaryShellRoute } from "@utils/onboarding/appAuxiliaryShellRoute";
import { isInviteLandingPath } from "@utils/invite/inviteCode";
import { isAdSkipSource } from "@utils/onboarding/adSource";
import { parseEdgenOnboardingStep } from "@utils/onboarding/edgenOnboardingStep";
import { PathHandler } from "./PathHandler";
import { isProtectedProductPath } from "./protectedProductPaths";

const DEVICE_SEGMENTS = new Set(["desktop", "ios", "android", "h5"]);

/** Proxy 豁免：登录相关旧链 + pricing */
const EXEMPT_PATH_PREFIXES = [
  "/login_popup",
  "/sign_in",
  ROUTER_PATH.LOGIN,
] as const;

/**
 * 去掉 locale 与 device 段，得到产品路由（如 `/zh/h5/brief` → `/brief`）。
 */
export function normalizeProductPathname(pathname: string): string {
  let path = PathHandler.removeLocalePrefix(pathname);
  const segments = path.split("/").filter(Boolean);
  if (segments.length > 0 && DEVICE_SEGMENTS.has(segments[0])) {
    path = PathHandler.joinSegments(segments.slice(1));
  }
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  return path === "" ? "/" : path;
}

export function isOnboardingStepGateExemptPath(
  normalizedPath: string,
): boolean {
  if (normalizedPath === "/") {
    return true;
  }

  if (isPricingRoute(normalizedPath)) {
    return true;
  }

  if (isNewsRoute(normalizedPath)) {
    return true;
  }

  if (isAppAuxiliaryShellRoute(normalizedPath)) {
    return true;
  }

  // 邀请落地页：307 到 /login 会把承载邀请码的 path 段一起丢掉（preserveSearch 只保 query），
  // 而被门禁拦下的正是「未登录的被邀请人」——邀请链接唯一的目标人群。见 isInviteLandingPath。
  if (isInviteLandingPath(normalizedPath)) {
    return true;
  }

  return EXEMPT_PATH_PREFIXES.some(
    (prefix) =>
      normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
}

function buildLocalizedRedirectPath(
  locale: string,
  targetPath: string,
): string {
  return PathHandler.buildPathWithLocale(targetPath, locale);
}

/**
 * 根据 pathname 与 step cookie 解析 307 目标；无需重定向时返回 null。
 *
 * ENG-WWW-SCHEMA-SOFT404-001：
 * - 无 cookie：仅受保护产品路径 → login；未知路径放行（真 404）
 * - step 1/2：仅已知产品路径拉回 onboarding；未知路径放行
 */
export function resolveOnboardingStepRedirectTarget(
  pathname: string,
  stepRaw: string | undefined,
  locale: string,
  adSourceRaw?: string | null | undefined,
): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const { isMainSite } = PathHandler.getLocaleFromPath(segments);
  if (isMainSite) {
    return null;
  }

  const normalizedPath = normalizeProductPathname(pathname);
  if (isOnboardingStepGateExemptPath(normalizedPath)) {
    return null;
  }

  const step = parseEdgenOnboardingStep(stepRaw);
  const isProtected = isProtectedProductPath(normalizedPath);

  if (step === null) {
    // 未登录：受保护路径 → login（不论 ad_source，必须先登录）
    if (!isProtected) {
      return null;
    }
    const loginPath = buildLocalizedRedirectPath(locale, ROUTER_PATH.LOGIN);
    return pathname === loginPath ? null : loginPath;
  }

  if (step === 1 || step === 2) {
    // 已登录未完成 onboarding：广告钩子来源跳过 onboarding，直接放行（仅在登录后生效）
    if (isAdSkipSource(adSourceRaw)) {
      return null;
    }
    if (isOnboardingRoute(normalizedPath)) {
      return null;
    }
    if (!isProtected) {
      return null;
    }
    return buildLocalizedRedirectPath(locale, ROUTER_PATH.ONBOARDING);
  }

  return null;
}
